import React from 'react';
import { Checkbox, FieldContainer, Input, Modal } from '@odin-labs/components';
import {
  AccessGroupSummaryFragment,
  useCreateAccessGroupMutation,
  useRenameAccessGroupMutation,
} from 'apollo/generated/client-operations';
import { AccessGroupActionFeedback } from './AccessGroupStatus';
import { useAccessGroupAction } from './useAccessGroupAction';

type Props = {
  siteId: string;
  group?: AccessGroupSummaryFragment;
  refresh: () => Promise<unknown>;
  onClose: () => void;
  onSaved?: (message: string) => void;
};

export function AccessGroupEditor({
  siteId,
  group,
  refresh,
  onClose,
  onSaved,
}: Props): React.ReactElement {
  const [name, setName] = React.useState(group?.name ?? '');
  const [makeDefault, setMakeDefault] = React.useState(false);
  const [create] = useCreateAccessGroupMutation();
  const [rename] = useRenameAccessGroupMutation();
  const action = useAccessGroupAction(refresh, onSaved);
  const save = async (): Promise<void> => {
    if (!name.trim()) return;
    const saved = await action.execute(
      () =>
        group
          ? rename({
              variables: {
                input: { siteId, accessGroupId: group.id, name: name.trim() },
              },
            })
          : create({
              variables: { input: { siteId, name: name.trim(), makeDefault } },
            }),
      'Group saved in Odin.',
    );
    if (saved) onClose();
  };
  return (
    <Modal
      open
      setOpen={(open) => {
        if (!open && !action.busy) onClose();
      }}
      title={group ? 'Rename access group' : 'Create access group'}
      size="sm"
      onAction={save}
      actionText={group ? 'Save name' : 'Create group'}
      actionButtonEnabled={Boolean(name.trim()) && !action.disabled}
      actionButtonWithSpinner={action.busy}
      cancelButtonEnabled={!action.busy}
    >
      <form
        className="odin-space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <FieldContainer
          name="access-group-name"
          label="Group name"
          description="Unique within this site. Names ignore case and surrounding spaces."
        >
          <Input
            id="access-group-name"
            name="access-group-name"
            value={name}
            onChange={setName}
            autoFocus
            disabled={action.disabled}
            required
          />
        </FieldContainer>
        {!group && (
          <Checkbox
            name="make-default"
            label="Make this the default group"
            checked={makeDefault}
            onChange={(event) => setMakeDefault(event.target.checked)}
            disabled={action.disabled}
          />
        )}
        {makeDefault && (
          <p className="odin-text-sm odin-text-gray-600">
            This replaces the current default. Existing memberships remain
            unchanged.
          </p>
        )}
        <AccessGroupActionFeedback action={action} />
      </form>
    </Modal>
  );
}
